import React, { useState } from 'react'
import { config } from '../config'

interface FAQItem {
  question: string
  answer: string
  category: string
}

interface FAQProps {
  onContactClick?: () => void
}

const FAQ: React.FC<FAQProps> = ({ onContactClick }) => {
  const [openItems, setOpenItems] = useState<number[]>([])
  const [activeCategory, setActiveCategory] = useState('all')

  const faqData: FAQItem[] = [
    {
      question: 'How do I start with Universal Pay?',
      answer: 'We have a super simple process for getting you started with Universal Pay. It all starts with you contacting us via the contact us form or through any of our social media channels! Once you reach out to us, one of our team will get back to you within 24 hours and assist you every step of the way! From this point on, we will get to know the services you require and advise you on the best course of action.',
      category: 'getting-started'
    },
    {
      question: 'Are there costs involved?',
      answer: 'There are costs when setting up your account with Universal Pay, but the costs vary depending on the services you require. In many cases however, we can waive any fees. We believe in full transparency and work hard to ensure our company is seen as a reliable and honest source of information and assistance, so before we start any cooperation our team will disclose any potential costs to you.',
      category: 'pricing'
    },
    {
      question: 'Do you accept high risk businesses?',
      answer: 'At Universal we have many solutions for many industries. Our actual niche is online gaming and Forex, but we can also facilitate payment solutions for crypto, Nutra, and many other high-risk industries. The way we usually work, is that we have an initial call or communication with you and from there we will be able to give you the best payment methods to support your business and grow your customer base.',
      category: 'business-types'
    },
    {
      question: 'How long does onboarding take?',
      answer: 'Set up mostly depends on you as the merchant. As a provider and consulting firm, we ensure that as soon as we have everything we need from you – our partners will fast track your integration. As we follow a time tested process, we usually say that onboarding with a new PSP takes around 5 working days, however if you as the merchant already have all documentation ready, set up can take as little as 2 days.',
      category: 'onboarding'
    },
    {
      question: 'What payment methods do you support?',
      answer: 'We support over 100 payment methods including major credit cards (Visa, Mastercard, American Express), digital wallets (Apple Pay, Google Pay, PayPal), bank transfers, cryptocurrency payments, and local payment methods specific to different regions. Our extensive network ensures global coverage.',
      category: 'payment-methods'
    },
    {
      question: 'Is my payment data secure?',
      answer: 'Absolutely! We employ bank-level security with PCI DSS compliance, advanced encryption, fraud detection systems, and real-time monitoring. Your customers\' payment data is protected with the highest industry standards and we never store sensitive card information.',
      category: 'security'
    },
    {
      question: 'What currencies do you support?',
      answer: 'We support 150+ currencies including all major fiat currencies (USD, EUR, GBP, JPY, etc.) and popular cryptocurrencies (Bitcoin, Ethereum, USDT, etc.). Our multi-currency support allows you to accept payments from customers worldwide and settle in your preferred currency.',
      category: 'currencies'
    },
    {
      question: 'Do you provide real-time reporting?',
      answer: 'Yes! Our advanced dashboard provides real-time transaction monitoring, detailed analytics, settlement reports, and customizable insights. You can track your payment performance, identify trends, and make data-driven decisions to optimize your business.',
      category: 'reporting'
    },
    {
      question: 'What are your transaction fees?',
      answer: 'Our fees are competitive and transparent, varying based on payment method, transaction volume, and business type. We offer volume discounts for high-volume merchants and custom pricing for enterprise clients. Contact us for a personalized quote based on your specific needs.',
      category: 'pricing'
    },
    {
      question: 'Do you offer 24/7 support?',
      answer: 'Yes! We provide 24/7 customer support through multiple channels including live chat, email, and phone. Our expert team is always available to help with technical issues, integration questions, or any payment-related concerns you may have.',
      category: 'support'
    }
  ]

  const categories = [
    { id: 'all', name: 'All Questions' },
    { id: 'getting-started', name: 'Getting Started' },
    { id: 'pricing', name: 'Pricing' },
    { id: 'business-types', name: 'Business Types' },
    { id: 'payment-methods', name: 'Payment Methods' },
    { id: 'security', name: 'Security' },
    { id: 'support', name: 'Support' }
  ]

  const filteredFAQs = activeCategory === 'all' 
    ? faqData 
    : faqData.filter(item => item.category === activeCategory)

  const toggleItem = (index: number) => {
    setOpenItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    )
  }

  return (
    <section id="faq" className="py-20 bg-brand-gray-dark relative overflow-hidden matrix-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Frequently Asked <span className="gradient-text">Questions</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Welcome to the Universal Pay FAQ section. Here, you'll find answers to the most common questions about our payment solutions, security, supported currencies, and transaction processing. Whether you're a new user or a business partner, we've got you covered!
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`px-6 py-2 rounded-full transition-all duration-300 ${
                activeCategory === category.id
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* FAQ Items */}
        <div className="max-w-4xl mx-auto">
          <div className="space-y-4">
            {filteredFAQs.map((item, index) => (
              <div 
                key={index}
                className="glass-card rounded-xl overflow-hidden transition-all duration-300 hover:shadow-lg"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <button
                  onClick={() => toggleItem(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-white/5 transition-colors duration-300"
                >
                  <h3 className="text-lg font-semibold text-white pr-4">
                    {item.question}
                  </h3>
                  <svg 
                    className={`w-6 h-6 text-brand-blue transition-transform duration-300 flex-shrink-0 ${
                      openItems.includes(index) ? 'rotate-180' : ''
                    }`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                <div className={`overflow-hidden transition-all duration-300 ${
                  openItems.includes(index) ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}>
                  <div className="px-6 pb-4">
                    <p className="text-gray-300 leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contact CTA */}
        <div className="text-center mt-16">
          <div className="glass-card rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">
              Still Have <span className="gradient-text">Questions</span>?
            </h3>
            <p className="text-gray-300 mb-6">
              Our expert team is available 24/7 to answer your questions and help you get started with Universal Pay.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onContactClick}
                className="cta-button neon-glow px-8 py-3 rounded-lg font-semibold text-white hover:shadow-xl transition-all duration-300"
              >
                Contact Us
              </button>
              <a
                href={config.links.getStarted}
                className="glass-card border border-brand-blue text-brand-blue px-8 py-3 rounded-lg font-semibold hover:bg-brand-blue hover:text-white transition-all duration-300 text-center inline-block"
              >
                Start Integration
              </a>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { number: '<24hr', label: 'Response Time' },
            { number: '24/7', label: 'Support Available' },
            { number: '100+', label: 'Payment Methods' },
            { number: '150+', label: 'Currencies Supported' }
          ].map((stat, index) => (
            <div 
              key={stat.label}
              className="text-center glass-card rounded-xl p-6 hover:scale-105 transition-all duration-300"
              style={{ animationDelay: `${(index + filteredFAQs.length) * 50}ms` }}
            >
              <div className="text-2xl font-bold gradient-text mb-2">{stat.number}</div>
              <div className="text-gray-400 text-sm">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default FAQ
